import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Camera, Upload, Trash, X } from '@phosphor-icons/react';

/**
 * Section "Photos design" pour le récap technicien d'une commande.
 * - Multi-upload (drag & drop ou clic)
 * - Grille de miniatures avec bouton supprimer
 * - Compteur du total (max 20)
 *
 * Les photos apparaîtront côté client dans le viewer 3D via un bouton
 * « Voir les photos » qui ouvre un modal diaporama plein écran.
 */
const MAX_PHOTOS = 20;
const MAX_FILE_MB = 10;
const ALLOWED_MIME = new Set(['image/jpeg', 'image/png', 'image/webp']);

export default function DiaporamaUploadSection({ orderId }) {
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const fetchImages = async () => {
    try {
      const res = await axios.get(`${API}/orders/${orderId}/diaporama`);
      setImages(res.data?.images || []);
    } catch (e) { /* silent, section optionnelle */ }
  };

  useEffect(() => { if (orderId) fetchImages(); }, [orderId]);

  const uploadFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter(Boolean);
    if (!files.length) return;
    // Validation locale : extension + taille
    const invalid = files.find(f => !ALLOWED_MIME.has(f.type) || f.size > MAX_FILE_MB * 1024 * 1024);
    if (invalid) {
      toast.error(`Fichier non valide : ${invalid.name} (JPEG/PNG/WEBP · max ${MAX_FILE_MB} Mo)`);
      return;
    }
    if (images.length + files.length > MAX_PHOTOS) {
      toast.error(`Limite ${MAX_PHOTOS} photos par commande (${images.length} déjà présentes)`);
      return;
    }
    const fd = new FormData();
    files.forEach(f => fd.append('files', f));
    setUploading(true);
    try {
      await axios.post(`${API}/orders/${orderId}/diaporama/upload`, fd);
      toast.success(`${files.length} photo(s) uploadée(s)`);
      fetchImages();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur upload');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleDelete = async (imgId) => {
    if (!window.confirm('Supprimer cette photo ?')) return;
    try {
      await axios.delete(`${API}/orders/${orderId}/diaporama/${imgId}`);
      toast.success('Photo supprimée');
      fetchImages();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur suppression');
    }
  };

  const handleDrop = (e) => {
    e.preventDefault(); e.stopPropagation(); setDragOver(false);
    uploadFiles(e.dataTransfer.files);
  };

  return (
    <div className="mt-4 border-t border-zinc-200 pt-4" data-testid="diaporama-section">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Camera size={16} className="text-zinc-600" />
          <h4 className="text-sm font-semibold text-zinc-800">Photos design</h4>
          <span className="text-xs text-zinc-500">{images.length} / {MAX_PHOTOS}</span>
        </div>
      </div>
      <p className="text-[11px] text-zinc-500 mb-2">
        Screenshots du logiciel de design (embout dans le moulage). Visibles pour le client depuis la page de visualisation 3D.
      </p>

      <div
        className={`border-2 border-dashed rounded-sm px-3 py-4 text-center cursor-pointer transition-colors ${
          dragOver ? 'border-blue-500 bg-blue-50' : 'border-zinc-300 bg-white hover:border-zinc-400 hover:bg-zinc-50'
        } ${uploading ? 'opacity-60 pointer-events-none' : ''}`}
        onClick={() => inputRef.current?.click()}
        onDrop={handleDrop}
        onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDragOver(true); }}
        onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setDragOver(false); }}
        data-testid="diaporama-dropzone"
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          multiple
          className="hidden"
          onChange={(e) => uploadFiles(e.target.files)}
          data-testid="diaporama-file-input"
        />
        <Upload size={20} className={`mx-auto mb-1 ${dragOver ? 'text-blue-500' : 'text-zinc-400'}`} />
        <p className="text-xs text-zinc-600 font-medium">
          {uploading ? 'Upload en cours…' : 'Glissez vos photos ici ou cliquez (JPEG · PNG · WEBP)'}
        </p>
      </div>

      {images.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2 mt-3" data-testid="diaporama-grid">
          {images.map(img => (
            <div key={img.id} className="relative group aspect-square rounded-sm overflow-hidden border border-zinc-200 bg-zinc-100" data-testid={`diaporama-thumb-${img.id}`}>
              <img
                src={`${API}/orders/${orderId}/diaporama/${img.id}/preview`}
                alt={img.filename}
                className="w-full h-full object-cover"
                loading="lazy"
              />
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); handleDelete(img.id); }}
                className="absolute top-1 right-1 p-1 rounded-sm bg-white/95 hover:bg-red-100 text-red-600 opacity-0 group-hover:opacity-100 transition-opacity shadow-sm"
                title="Supprimer"
                data-testid={`diaporama-delete-${img.id}`}
              >
                <Trash size={12} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
